import { useEffect, useRef } from 'react';

import type { NoharaRepresentative } from '../../types/company.types';
import type { ValidationResult } from './types';

interface UseMarkInitialDetailFieldsTouchedProps<
  T extends { representatives: NoharaRepresentative[] },
> {
  formData: T;
  touchedFields: Set<string>;
  setTouchedFields: (fields: Set<string>) => void;
  hasFetchedData: ReturnType<typeof useRef<boolean>>;
  validateForm: (formData: T) => ValidationResult;
}

/**
 * Common hook for marking initial fields as touched in detail forms after data is loaded
 * Marks fields with validation errors and all representative fields as touched
 * Only runs once after data is fetched
 */
export const useMarkInitialDetailFieldsTouched = <
  T extends { representatives: NoharaRepresentative[] },
>({
  formData,
  touchedFields,
  setTouchedFields,
  hasFetchedData,
  validateForm,
}: UseMarkInitialDetailFieldsTouchedProps<T>) => {
  const hasMarkedInitialFields = useRef(false);

  useEffect(() => {
    // Only run after data is loaded and once
    if (!hasFetchedData.current || hasMarkedInitialFields.current) {
      return;
    }

    // 1) Run validation on current formData to get all field errors
    const initialValidation = validateForm(formData);

    // 2) Extract all field names that have errors (these are the fields that need to be shown as touched)
    const fieldsWithErrors = initialValidation.errors.map((error) => error.field);

    // 3) Update touched fields with fields that have errors
    const newTouched = new Set<string>(touchedFields);
    for (const field of fieldsWithErrors) {
      newTouched.add(field);
    }

    // 4) ALWAYS mark all representative fields as touched when data is loaded
    // This ensures validation messages show immediately for all representatives
    formData.representatives.forEach((_, index) => {
      newTouched.add(`representatives.${index}.department`);
      newTouched.add(`representatives.${index}.salesPerson`);
    });

    // 5) Update touched fields once
    setTouchedFields(newTouched);

    hasMarkedInitialFields.current = true;
  }, [formData, touchedFields, hasFetchedData, validateForm, setTouchedFields]);
};
